"use client";

import { useMemo } from "react";
import { usePathname } from "next/navigation";
import Script from "next/script";
import { PATHS } from "@/internals/constants/paths";
import { stripLocaleFromPath } from "@/utils/locale";
import { DatadogInit, logError } from "@/services/DatadogService/DatadogService";
import { APPLE_SSO_SDK_URL } from "@/utils/SSOConfig";

interface ThirdPartyScriptsProps {
  gtmId: string;
}

export const ThirdPartyScripts = ({ gtmId }: ThirdPartyScriptsProps) => {
  const pathname = usePathname();
  const shouldLoadAppleSDK = useMemo(() => {
    const pathWithoutLang = stripLocaleFromPath(pathname || "/");
    return pathWithoutLang.startsWith(PATHS.LOGIN) || pathWithoutLang.startsWith(PATHS.SIGNUP);
  }, [pathname]);

  return (
    <>
      {/* Datadog */}
      <DatadogInit />

      {/* Apple SSO SDK */}
      {shouldLoadAppleSDK && (
        <Script
          src={APPLE_SSO_SDK_URL}
          strategy="lazyOnload"
          id="apple-signin-sdk"
          onError={(e) => {
            logError(new Error("Apple SDK failed to load"), {
              context: "ThirdPartyScripts",
              details: e,
            });
          }}
        />
      )}

      {/* Google Tag Manager (noscript) */}
      <noscript>
        <iframe
          src={`https://www.googletagmanager.com/ns.html?id=${gtmId}`}
          height="0"
          width="0"
          style={{ display: "none", visibility: "hidden" }}
        ></iframe>
      </noscript>
    </>
  );
};
